import { Formik } from "formik";
import React, { useState, useEffect } from "react";
import { withRouter } from "react-router-dom";
import { toast, ToastBar, Toaster } from "react-hot-toast";
import * as yup from "yup";
import {
  addEditPricingEquipment,
  getTemplateForApplication,
  getPreappById,
  getMerchantInfo,
  getUserProfile,
  sendApplicationLinkToMerchant,
  getOrganisationBanks,
} from "../../../api";
import { otherDefaultFeesPrettyNames } from "../../../components/modals/add-new-application-modal/pricing-validation";
import config from "../../../config";
import Breadcrumbs from "../../../v2/components/Breadcrumbs";
import Alert from "../../../v2/components/alert";
import useUserStore from "../../../v2/stores/user";
import useDomainStore from "../../../v2/stores/domain";
import useOrganizationStore, {
  fetchPricingDefaults,
} from "../../../v2/stores/organization";
import ButtonIcon from "../../components/ButtonIcon/ButtonIcon";
import Select from "../../components/form/select";
import SkeletonLoader from "../../components/skeleton-loader";
import Cost from "../create-app/components/cost";
import Equipments from "../create-app/components/equipments";
import SendLinkMerchantModal from "../create-app/components/merchant-link-modal";
import Pricing from "../create-app/components/pricing";
import Details from "../create-app/components/details";
import Processors from "../create-app/components/processor";
import ConfirmationModal from "../../pages/application/components/ConfirmationModal/ConfirmationModal";
import { useEquipmentsByTypeAndBrand } from "../create-app/components/equipments/useEquipmentsByTypeAndBrand";
import MCCAndEMSOfficeId from "./components/ems-and-mcc-id";
import {
  equipmentSchema,
  createPricingInfoSchema,
  detailsInfoSchema,
  processorInfoSchema,
} from "../create-app/validation-schema";
import { scrollToError } from "../create-app/utils/scroll-to-error";
import { getDefaultPricingModelValuesExceptActive } from "../create-app/utils/pricing-defaults";
import styles from "../create-app/style.module.scss";
import { shouldConfirmOnlyKurvEquipment } from "../create-app/utils/kurv-equipment-confirmation";
import { isAppArchived } from "../../utils/is-app-archived";
import ArchiveApp from "../application/components/ArchiveApp/ArchiveApp";
import CopyApplication from "../application/components/CopyApplication/Index";

// override_bank stores bank names; processors form state stores bank IDs.
const mapOverrideBankToProcessorIds = (overrideBank, processorList) => {
  if (!Array.isArray(overrideBank) || !overrideBank.length) return [];
  if (!Array.isArray(processorList) || !processorList.length) return [];

  return overrideBank
    .map((bank) => {
      const bankName = bank?.label ?? bank?.value;
      const match = processorList.find((p) => p.label === bankName);
      return match ? Number(match.value) : null;
    })
    .filter((id) => id !== null);
};

const createFormSchema = (pricingDefaults) =>
  yup.object({
    ...createPricingInfoSchema(pricingDefaults, {
      requirePaylianceIndustry: true,
    }).fields,
    ...equipmentSchema,
    ...detailsInfoSchema.fields,
    ...processorInfoSchema.fields,
  });

const EditPricingEquipmentPage = ({ history, match }) => {
  const { isKurvDomain } = useDomainStore();
  const organizationId = useUserStore((state) => state.organizationId);
  const agentUrl = useUserStore((state) => state.agentUrl);
  const user = useUserStore((state) => state.user);
  const isPCUser = useUserStore((state) => state.isPCUser);
  const isKurvUser = useUserStore((state) => state.isKurvUser);
  const isEntitySuperAdmin = useUserStore((state) => state.isEntitySuperAdmin);
  const pricingDefaults = useOrganizationStore(
    (state) => state.pricingDefaults,
  );
  const pricingDefaultsLoading = useOrganizationStore(
    (state) => state.pricingDefaultsLoading,
  );
  const [continueURL, setContinueURL] = useState();
  const [submissionType, setSubmissionType] = useState("");
  const [sendLinkModal, setSendLinkModal] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [buttonText, setButtonText] = useState("Save");
  const [openSections, setOpenSections] = useState({
    pricing: true,
    equipments: true,
    details: true,
    mccAndEMSOfficeId: true,
    processors: true,
  });
  const [templateList, setTemplateList] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [appData, setAppData] = useState(null);
  const [appDataLoading, setAppDataLoading] = useState(true);
  const [templateListLoading, setTemplateListLoading] = useState(true);
  const [openArchiveApp, setOpenArchiveApp] = useState(false);
  const [confirmCopyApplication, setConfirmCopyApplication] = useState(false);
  const [isArchivedApp, setIsArchivedApp] = useState(false);
  const [sendingLinkToMerchant, setSendingLinkToMerchant] = useState(false);
  const [showSendToMerchantConfirm, setShowSendToMerchantConfirm] =
    useState(false);
  const [isSendToMerchantModalOpen, setIsSendToMerchantModalOpen] =
    useState(false);
  const [applicationURL, setApplicationURL] = useState("");

  const handleArchiveClose = () => {
    setOpenArchiveApp(false);
    history.push(`/v2/applications`);
  };

  const [emsOfficeOptions, setEMSOfficeOptions] = useState([]);
  const fetchEMSOffices = async () => {
    try {
      const userRes = await getUserProfile();
      if (userRes.success) {
        if (userRes.data.ems_office && userRes.data.ems_office?.length > 0) {
          setEMSOfficeOptions(userRes.data.ems_office);
        } else {
          setEMSOfficeOptions([]);
        }
      }
    } catch (error) {
      console.error("Failed to fetch EMS offices:", error);
    }
  };

  const handleSendToMerchant = () => {
    if (!appData?.editslug) {
      toast.error("Application not found. Please save the application first.");
      return;
    }
    setShowSendToMerchantConfirm(true);
  };

  const confirmSendToMerchant = async () => {
    setShowSendToMerchantConfirm(false);
    setSendingLinkToMerchant(true);

    try {
      const res = await sendApplicationLinkToMerchant(appData?.editslug);
      if (res?.status) {
        setIsSendToMerchantModalOpen(true);
        setApplicationURL(res?.url);
      } else {
        toast.error(
          res?.message || "Failed to send application link to merchant",
        );
      }
    } catch (err) {
      console.error("Error sending application link to merchant", err);
      toast.error("Error sending application link to merchant");
    } finally {
      setSendingLinkToMerchant(false);
    }
  };

  useEffect(() => {
    if (organizationId) {
      fetchPricingDefaults(organizationId);
    }
    fetchEMSOffices();
  }, [organizationId]);

  // Create a function to get initial values instead of using state
  const getInitialValues = (pricingDefaults = {}) => ({
    // pricing fields
    pricing_model: "",
    pricing_approved_by_rate_desk: "no",
    // Dual pricing fields
    dual_pricing_rate:
      pricingDefaults?.dual_pricing?.dual_pricing_rate?.default ?? "",
    dual_pricing_per_transaction:
      pricingDefaults?.dual_pricing?.dual_pricing_per_transaction?.default ??
      "",
    dual_pricing_monthly_fee:
      pricingDefaults?.dual_pricing?.dual_pricing_monthly_fee?.default ?? "",
    dual_pricing_pin_debit_network_fee: "no",
    dual_pricing_pin_debit_rate:
      pricingDefaults?.dual_pricing?.dual_pricing_pin_debit_rate?.default ?? "",
    dual_pricing_pin_debit_per_transaction:
      pricingDefaults?.dual_pricing?.dual_pricing_pin_debit_per_transaction
        ?.default ?? "",

    // surcharge
    surcharge_rate: pricingDefaults?.surcharge?.surcharge_rate?.default ?? "",
    surcharge_per_transaction:
      pricingDefaults?.surcharge?.surcharge_per_transaction?.default ?? "",
    surcharge_monthly_fee:
      pricingDefaults?.surcharge?.surcharge_monthly_fee?.default ?? "",
    surcharge_pin_debit_network_fee:
      pricingDefaults?.surcharge?.surcharge_pin_debit_network_fee?.default ??
      "no",
    surcharge_pin_debit_rate:
      pricingDefaults?.surcharge?.surcharge_pin_debit_rate?.default ?? "",
    surcharge_pin_debit_per_transaction:
      pricingDefaults?.surcharge?.surcharge_pin_debit_per_transaction
        ?.default ?? "",
    surcharge_qual_debit_rate:
      pricingDefaults?.surcharge?.surcharge_qual_debit_rate?.default ?? "",
    surcharge_qual_debit_per_transaction:
      pricingDefaults?.surcharge?.surcharge_qual_debit_per_transaction
        ?.default ?? "",
    surcharge_mid_qual_debit_rate:
      pricingDefaults?.surcharge?.surcharge_mid_qual_debit_rate?.default ?? "",
    surcharge_mid_qual_debit_per_transaction:
      pricingDefaults?.surcharge?.surcharge_mid_qual_debit_per_transaction
        ?.default ?? "",
    surcharge_non_qual_debit_rate:
      pricingDefaults?.surcharge?.surcharge_non_qual_debit_rate?.default ?? "",
    surcharge_non_qual_debit_per_transaction:
      pricingDefaults?.surcharge?.surcharge_non_qual_debit_per_transaction
        ?.default,
    surcharge_signature_debit_rate:
      pricingDefaults?.surcharge?.surcharge_signature_debit_rate?.default ?? "",
    surcharge_signature_debit_per_transaction:
      pricingDefaults?.surcharge?.surcharge_signature_debit_per_transaction
        ?.default ?? "",

    // Flat Rate
    flat_rate: pricingDefaults?.flat_rate?.flat_rate?.default ?? "",
    flat_rate_transaction:
      pricingDefaults?.flat_rate?.flat_rate_transaction?.default ?? "",
    flat_rate_amex: pricingDefaults?.flat_rate?.flat_rate_amex?.default ?? "",
    flat_rate_amex_transaction:
      pricingDefaults?.flat_rate?.flat_rate_amex_transaction?.default ?? "",
    flat_rate_pin_debit_network_fee: "no",
    flat_rate_pin_debit_rate:
      pricingDefaults?.flat_rate?.flat_rate_pin_debit_rate?.default ?? "",
    flat_rate_pin_debit_per_transaction:
      pricingDefaults?.flat_rate?.flat_rate_pin_debit_per_transaction
        ?.default ?? "",
    flat_rate_amex_toggle: "yes",

    // IC+
    ic_plus_rate: pricingDefaults?.ic_plus?.ic_plus_rate?.default ?? "",
    ic_plus_transaction_fee:
      pricingDefaults?.ic_plus?.ic_plus_transaction_fee?.default ?? "",
    ic_plus_amex_rate:
      pricingDefaults?.ic_plus?.ic_plus_amex_rate?.default ?? "",
    ic_plus_amex_transaction_fee:
      pricingDefaults?.ic_plus?.ic_plus_amex_transaction_fee?.default ?? "",
    ic_plus_pin_debit_network_fee: "no",
    ic_plus_pin_debit_rate:
      pricingDefaults?.ic_plus?.ic_plus_pin_debit_rate?.default ?? "",
    ic_plus_pin_debit_per_transaction:
      pricingDefaults?.ic_plus?.ic_plus_pin_debit_per_transaction?.default ??
      "",

    // tiered
    tiered_qual_rate: pricingDefaults?.tiered?.tiered_qual_rate?.default ?? "",
    tiered_qual_transaction:
      pricingDefaults?.tiered?.tiered_qual_transaction?.default ?? "",
    tiered_qual_debit_rate:
      pricingDefaults?.tiered?.tiered_qual_debit_rate?.default ?? "",
    tiered_qual_debit_per_transaction:
      pricingDefaults?.tiered?.tiered_qual_debit_per_transaction?.default ?? "",
    tiered_mid_qual_rate:
      pricingDefaults?.tiered?.tiered_mid_qual_rate?.default ?? "",
    tiered_mid_qual_transaction:
      pricingDefaults?.tiered?.tiered_mid_qual_transaction?.default ?? "",
    tiered_mid_qual_debit_rate:
      pricingDefaults?.tiered?.tiered_mid_qual_debit_rate?.default ?? "",
    tiered_mid_qual_debit_per_transaction:
      pricingDefaults?.tiered?.tiered_mid_qual_debit_per_transaction?.default ??
      "",
    tiered_non_qual_rate:
      pricingDefaults?.tiered?.tiered_non_qual_rate?.default ?? "",
    tiered_non_qual_transaction:
      pricingDefaults?.tiered?.tiered_non_qual_transaction?.default ?? "",
    tiered_non_qual_debit_rate:
      pricingDefaults?.tiered?.tiered_non_qual_debit_rate?.default ?? "",
    tiered_non_qual_debit_per_transaction:
      pricingDefaults?.tiered?.tiered_non_qual_debit_per_transaction?.default ??
      "",
    tiered_pin_debit_network_fee: "no",
    tiered_pin_debit_rate:
      pricingDefaults?.tiered?.tiered_pin_debit_rate?.default ?? "",
    tiered_pin_debit_per_transaction:
      pricingDefaults?.tiered?.tiered_pin_debit_per_transaction?.default ?? "",

    tiered_amex_structure: "tiered", // tiered, flat_rate, ic_plus

    amex_tier_one_rate:
      pricingDefaults?.tiered?.amex_tier_one_rate?.default ?? "",
    amex_tier_one_fee:
      pricingDefaults?.tiered?.amex_tier_one_fee?.default ?? "",
    amex_tier_two_rate:
      pricingDefaults?.tiered?.amex_tier_two_rate?.default ?? "",
    amex_tier_two_fee:
      pricingDefaults?.tiered?.amex_tier_two_fee?.default ?? "",
    amex_tier_three_rate:
      pricingDefaults?.tiered?.amex_tier_three_rate?.default ?? "",
    amex_tier_three_fee:
      pricingDefaults?.tiered?.amex_tier_three_fee?.default ?? "",
    amex_tier_four_rate:
      pricingDefaults?.tiered?.amex_tier_four_rate?.default ?? "",
    amex_tier_four_fee:
      pricingDefaults?.tiered?.amex_tier_four_fee?.default ?? "",

    tiered_amex_flat_rate:
      pricingDefaults?.tiered?.tiered_amex_flat_rate?.default ?? "",
    tiered_amex_flat_transaction:
      pricingDefaults?.tiered?.tiered_amex_flat_transaction?.default ?? "",

    tiered_amex_ic_plus_rate:
      pricingDefaults?.tiered?.tiered_amex_ic_plus_rate?.default ?? "",
    tiered_amex_ic_plus_transaction:
      pricingDefaults?.tiered?.tiered_amex_ic_plus_transaction?.default ?? "",
    tiered_type: pricingDefaults?.tiered?.tiered_type?.default ?? "swiped",

    //ach
    ach_discount_rate: pricingDefaults?.ach?.ach_discount_rate?.default ?? "",
    ach_transaction_fee_per_item:
      pricingDefaults?.ach?.ach_transaction_fee_per_item?.default ?? "",
    ach_statement_fee: pricingDefaults?.ach?.ach_statement_fee?.default ?? "",
    ach_monthly_minimum:
      pricingDefaults?.ach?.ach_monthly_minimum?.default ?? "",
    ach_return_fee: pricingDefaults?.ach?.ach_return_fee?.default ?? "",
    ach_noc_fee: pricingDefaults?.ach?.ach_noc_fee?.default ?? "",
    ach_unauthorized_return_fee:
      pricingDefaults?.ach?.ach_unauthorized_return_fee?.default ?? "",
    ach_same_day_fee: pricingDefaults?.ach?.ach_same_day_fee?.default ?? "",
    ach_bank_validation_fee:
      pricingDefaults?.ach?.ach_bank_validation_fee?.default ?? "",
    ach_by_web: "no",
    ach_by_phone: "no",
    ach_by_preauth: "no",
    ach_credit_transaction: "no",
    add_ach: "no",

    // kurv pricing
    kurv_pricing_swiped_rate:
      pricingDefaults?.kurv_pricing?.kurv_pricing_swiped_rate?.default ?? "",
    kurv_pricing_swiped_txn_fee:
      pricingDefaults?.kurv_pricing?.kurv_pricing_swiped_txn_fee?.default ?? "",
    kurv_pricing_keyed_rate:
      pricingDefaults?.kurv_pricing?.kurv_pricing_keyed_rate?.default ?? "",
    kurv_pricing_keyed_txn_fee:
      pricingDefaults?.kurv_pricing?.kurv_pricing_keyed_txn_fee?.default ?? "",
    kurv_pricing_amex_toggle: "yes",
    kurv_pricing_amex_rate:
      pricingDefaults?.kurv_pricing?.kurv_pricing_amex_rate?.default ?? "",
    kurv_pricing_amex_txn_fee:
      pricingDefaults?.kurv_pricing?.kurv_pricing_amex_txn_fee?.default ?? "",
    kurv_pricing_pin_debit_network_fee: "yes",
    kurv_pricing_pin_debit_rate:
      pricingDefaults?.kurv_pricing?.kurv_pricing_pin_debit_rate?.default ?? "",
    kurv_pricing_pin_debit_per_transaction:
      pricingDefaults?.kurv_pricing?.kurv_pricing_pin_debit_per_transaction
        ?.default ?? "",

    //Note: Below field is not used anywhere, but kept so as not to break anything.
    monthly_fee: "49.00",

    // ebt
    ebt: "no",
    fnf_number: "",

    // other default fees
    ...Object.keys(otherDefaultFeesPrettyNames).reduce((acc, key) => {
      acc[key] = pricingDefaults?.default_fees?.[key]?.default;
      return acc;
    }, {}),

    var_sheet_request: false,

    var_only: {},

    equipments: [],
    other_gateway: "",
    // Template tracking
    templateApplied: false,
    nonEditableTemplateApplied: false,
    processorSelectionLocked: false,
    template_id: null,

    who_to_deploy_equipment: "",
    who_to_pay_for_equipment: "",
    who_to_deploy_gateway: "",
    billing_sequence: "daily",
    cover_cancellation: "no",
    reimbursement_maximum: "",
    expedited_funding: "no",
    batch_time: "06:00 PM",
    equipment_address: "dba_address",
    invoice_address: "agent_address",
    special_instructions: "",
    uw_notes: "",
    is_guarantor: true,
    mcc_id: null,
    ems_office_id: null,

    // processor selection
    processors: [],
    payliance_industry: "",
    isPCUser,
  });

  const [initialValues, setInitialValues] = useState(
    getInitialValues(pricingDefaults),
  );

  const [initialValuesBackup, setInitialValuesBackup] = useState(
    getInitialValues(pricingDefaults),
  );
  const [isTemplateChanged, setIsTemplateChanged] = useState(false);

  const { equipments } = useEquipmentsByTypeAndBrand();

  const onSubmit = async (values, { setTouched }) => {
    //Note: Touching all fields so that the errors are shown
    setTouched({});
    if (submissionType == "pe_send_to_merchant") {
      setSendingLinkToMerchant(true);
    }

    const dataToSubmit = { ...values };
    dataToSubmit.submission_type =
      submissionType === "continue" ? "create_app" : submissionType;

    if (!appData?.organization?.enable_personal_guarantor_toggle) {
      delete dataToSubmit.is_guarantor;
    }

    let res;
    try {
      res = await addEditPricingEquipment({
        ...dataToSubmit,
        powerapp_id: appData?.id,
      });
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update pricing and equipment");
      setSendingLinkToMerchant(false);
      return;
    }

    if (res.status) {
      if (res?.pending_approval) {
        toast.success("Saved. Awaiting Manager Approval before this can be sent for signature.");
      } else {
        toast.success("Pricing and equipment updated successfully");
      }
      if (submissionType == "pe_send_to_merchant") {
        handleSendToMerchant();
      } else {
        history.push(`/applications`);
      }
    } else {
      toast.error(res.message || "Failed to update pricing and equipment");
      setSendingLinkToMerchant(false);
    }
  };

  useEffect(() => {
    getTemplateForApplication().then((res) => {
      if (res.status) {
        setTemplateListLoading(false);
        setTemplateList(res.data);
        setTemplateOptions(
          res.data.map((item) => ({
            label: item.name,
            value: item.id,
          })),
        );
      }
    });
  }, []);

  const isV1Template = (template) => {
    return !Array.isArray(template?.data?.equipments);
  };

  const applyTemplate = (value, setValues) => {
    const template = templateList.find((item) => item.id === value);

    if (!template) {
      return;
    }

    const equipments = isV1Template(template)
      ? Object.entries(template?.data?.equipments).flatMap(
          ([equipmentType, equipmentGroups]) => {
            return Object.entries(equipmentGroups).flatMap(
              ([, equipmentList]) => {
                return Array.isArray(equipmentList)
                  ? equipmentList.map((equipment) => ({
                      ...equipment,
                      type: equipmentType,
                    }))
                  : [];
              },
            );
          },
        )
      : template?.data?.equipments;

    const templatePricingModel = template?.data?.qualifiedtype;
    const templateHasProcessors =
      Array.isArray(template?.data?.processors) &&
      template.data.processors.length > 0;

    setValues({
      ...getInitialValues(pricingDefaults),
      ebt: template?.data?.ebt || "no",
      is_tm: template?.data?.is_tm || "yes",
      avs_fee:
        template?.data?.avs_fee ||
        (pricingDefaults?.[templatePricingModel]?.avs_fee?.default ??
          pricingDefaults?.default_fees?.avs_fee?.default) ||
        "0.10",
      country: template?.data?.country || "en",
      etf_fee:
        template?.data?.etf_fee ||
        (pricingDefaults?.[templatePricingModel]?.etf_fee?.default ??
          pricingDefaults?.default_fees?.etf_fee?.default) ||
        "595.00",
      tm_lead: template?.data?.tm_lead || "no",
      var_only: template?.data?.var_only || null,
      batch_fee:
        template?.data?.batch_fee ||
        (pricingDefaults?.[templatePricingModel]?.batch_fee?.default ??
          pricingDefaults?.default_fees?.batch_fee?.default) ||
        "0.30",
      cold_call: template?.data?.cold_call || "no",
      flat_rate:
        template?.data?.flat_rate ||
        pricingDefaults?.flat_rate?.flat_rate?.default ||
        "3.00",
      setup_fee:
        template?.data?.setup_fee ||
        (pricingDefaults?.[templatePricingModel]?.setup_fee?.default ??
          pricingDefaults?.default_fees?.setup_fee?.default) ||
        "125.00",
      voice_aru:
        template?.data?.voice_aru ||
        (pricingDefaults?.[templatePricingModel]?.voice_aru?.default ??
          pricingDefaults?.default_fees?.voice_aru?.default) ||
        "0.60",
      batch_time: template?.data?.batch_time || "06:00 PM",
      fnf_number: template?.data?.fnf_number || null,
      data_breach:
        template?.data?.data_breach ||
        (pricingDefaults?.[templatePricingModel]?.data_breach?.default ??
          pricingDefaults?.default_fees?.data_breach?.default) ||
        "15.00",
      monthly_fee:
        template?.data?.monthly_fee ||
        pricingDefaults?.dual_pricing?.dual_pricing_monthly_fee?.default ||
        "49.00",
      postal_code: template?.data?.postal_code || null,
      selectedOrg: template?.data?.selectedOrg || null,
      ic_plus_rate:
        template?.data?.ic_plus_rate ||
        pricingDefaults?.ic_plus?.ic_plus_rate?.default ||
        "0.15",
      referrer_mid: template?.data?.referrer_mid || "",
      selectedUser: template?.data?.selectedUser || null,
      other_gateway: template?.data?.other_gateway || null,
      pricing_model: template?.data?.qualifiedtype || "dual_pricing",
      referrer_name: template?.data?.referrer_name || "",
      chargeback_fee:
        template?.data?.chargeback_fee ||
        (pricingDefaults?.[templatePricingModel]?.chargeback_fee?.default ??
          pricingDefaults?.default_fees?.chargeback_fee?.default) ||
        "45.00",
      flat_rate_amex:
        template?.data?.flat_rate_amex ||
        pricingDefaults?.flat_rate?.flat_rate_amex?.default ||
        "3.00",
      surcharge_rate:
        template?.data?.surcharge_rate ||
        pricingDefaults?.surcharge?.surcharge_rate?.default ||
        "3.00",
      invoice_address: template?.data?.invoice_address || "home_address",
      monthly_minimum:
        template?.data?.monthly_minimum ||
        (pricingDefaults?.[templatePricingModel]?.monthly_minimum?.default ??
          pricingDefaults?.default_fees?.monthly_minimum?.default) ||
        "25.00",
      pre_dispute_fee:
        template?.data?.pre_dispute_fee ||
        (pricingDefaults?.[templatePricingModel]?.pre_dispute_fee?.default ??
          pricingDefaults?.default_fees?.pre_dispute_fee?.default) ||
        "45.00",

      retrieval_fee:
        template?.data?.retrieval_fee ??
        pricingDefaults?.[templatePricingModel]?.retrieval_fee?.default ??
        "25.00",

      auth_transaction:
        template?.data?.auth_transaction ||
        (pricingDefaults?.[templatePricingModel]?.auth_transaction?.default ??
          pricingDefaults?.default_fees?.auth_transaction?.default) ||
        "0.25",
      billing_sequence: template?.data?.billing_sequence || "daily",
      ebt_transactions:
        template?.data?.ebt_transactions ||
        (pricingDefaults?.[templatePricingModel]?.ebt_transactions?.default ??
          pricingDefaults?.default_fees?.ebt_transactions?.default) ||
        0,
      monthly_cellular:
        template?.data?.monthly_cellular ||
        (pricingDefaults?.[templatePricingModel]?.monthly_cellular?.default ??
          pricingDefaults?.default_fees?.monthly_cellular?.default) ||
        "25.00",
      tiered_qual_rate:
        template?.data?.tiered_qual_rate ||
        pricingDefaults?.tiered?.tiered_qual_rate?.default ||
        "1.75",
      amex_tier_one_fee:
        template?.data?.amex_tier_one_fee ||
        pricingDefaults?.tiered?.amex_tier_one_fee?.default ||
        "0.20",
      amex_tier_two_fee: template?.data?.amex_tier_two_fee ?? "",
      dual_pricing_rate:
        template?.data?.dual_pricing_rate ||
        pricingDefaults?.dual_pricing?.dual_pricing_rate?.default ||
        4,
      equipment_address: template?.data?.equipment_address || "legal_address",
      expedited_funding: template?.data?.expedited_funding || "yes",
      ic_plus_amex_rate:
        template?.data?.ic_plus_amex_rate ||
        pricingDefaults?.ic_plus?.ic_plus_amex_rate?.default ||
        "0.15",
      representment_fee:
        template?.data?.representment_fee ||
        (pricingDefaults?.[templatePricingModel]?.representment_fee?.default ??
          pricingDefaults?.default_fees?.representment_fee?.default) ||
        "45.00",
      returned_ach_item:
        template?.data?.returned_ach_item ||
        (pricingDefaults?.[templatePricingModel]?.returned_ach_item?.default ??
          pricingDefaults?.default_fees?.returned_ach_item?.default) ||
        "25.00",
      template_id: template?.id || null,
      telemarketer_name: template?.data?.telemarketer_name || "",
      var_sheet_request: template?.data?.var_sheet_request || "yes",
      amex_tier_four_fee: template?.data?.amex_tier_four_fee ?? "",
      amex_tier_one_rate:
        template?.data?.amex_tier_one_rate ||
        pricingDefaults?.tiered?.amex_tier_one_rate?.default ||
        "3.50",
      amex_tier_two_rate: template?.data?.amex_tier_two_rate ?? "",
      cover_cancellation: template?.data?.cover_cancellation || "no",
      monthly_access_fee:
        template?.data?.monthly_access_fee ||
        (pricingDefaults?.[templatePricingModel]?.monthly_access_fee?.default ??
          pricingDefaults?.default_fees?.monthly_access_fee?.default) ||
        "15.00",
      amex_tier_four_rate: template?.data?.amex_tier_four_rate ?? "",
      amex_tier_three_fee: template?.data?.amex_tier_three_fee ?? "",
      reprogram_equipment: template?.data?.reprogram_equipment || true,
      amex_tier_three_rate: template?.data?.amex_tier_three_rate ?? "",
      cellular_transaction:
        template?.data?.cellular_transaction ||
        (pricingDefaults?.[templatePricingModel]?.cellular_transaction
          ?.default ??
          pricingDefaults?.default_fees?.cellular_transaction?.default) ||
        "0.10",
      is_merchant_referral: template?.data?.is_merchant_referral || "no",
      semi_annual_tech_fee:
        template?.data?.semi_annual_tech_fee ||
        (pricingDefaults?.[templatePricingModel]?.semi_annual_tech_fee
          ?.default ??
          pricingDefaults?.default_fees?.semi_annual_tech_fee?.default) ||
        "75.00",
      special_instructions: template?.data?.special_instructions || "",
      tiered_mid_qual_rate: template?.data?.tiered_mid_qual_rate ?? "",
      tiered_non_qual_rate: template?.data?.tiered_non_qual_rate ?? "",
      flat_rate_transaction:
        template?.data?.flat_rate_transaction ||
        pricingDefaults?.flat_rate?.flat_rate_transaction?.default ||
        "0.15",
      surcharge_monthly_fee:
        template?.data?.surcharge_monthly_fee ||
        pricingDefaults?.surcharge?.surcharge_monthly_fee?.default ||
        "49.00",
      tiered_amex_flat_rate:
        template?.data?.tiered_amex_flat_rate ||
        pricingDefaults?.tiered?.tiered_amex_flat_rate?.default ||
        "3.50",
      tiered_pin_debit_rate:
        template?.data?.tiered_pin_debit_rate ||
        pricingDefaults?.tiered?.tiered_pin_debit_rate?.default ||
        "0.00",
      who_to_deploy_gateway: template?.data?.who_to_deploy_gateway || "agent",
      ic_plus_pin_debit_rate:
        template?.data?.ic_plus_pin_debit_rate ||
        pricingDefaults?.ic_plus?.ic_plus_pin_debit_rate?.default ||
        "0.00",
      tiered_qual_debit_rate:
        template?.data?.tiered_qual_debit_rate ||
        pricingDefaults?.tiered?.tiered_qual_debit_rate?.default ||
        "1.50",
      ic_plus_transaction_fee:
        template?.data?.ic_plus_transaction_fee ||
        pricingDefaults?.ic_plus?.ic_plus_transaction_fee?.default ||
        "0.15",
      tiered_qual_transaction:
        template?.data?.tiered_qual_transaction ||
        pricingDefaults?.tiered?.tiered_qual_transaction?.default ||
        "0.15",
      who_to_deploy_equipment:
        template?.data?.who_to_deploy_equipment || "agent",
      dual_pricing_monthly_fee:
        template?.data?.dual_pricing_monthly_fee ||
        pricingDefaults?.dual_pricing?.dual_pricing_monthly_fee?.default ||
        "49.00",
      flat_rate_pin_debit_rate:
        template?.data?.flat_rate_pin_debit_rate ||
        pricingDefaults?.flat_rate?.flat_rate_pin_debit_rate?.default ||
        "2.00",
      reprogram_equipment_name:
        template?.data?.reprogram_equipment_name || null,
      surcharge_pin_debit_rate:
        template?.data?.surcharge_pin_debit_rate ||
        pricingDefaults?.surcharge?.surcharge_pin_debit_rate?.default ||
        "0.00",
      tiered_amex_ic_plus_rate:
        template?.data?.tiered_amex_ic_plus_rate ||
        pricingDefaults?.tiered?.tiered_amex_ic_plus_rate?.default ||
        "3.50",
      who_to_pay_for_equipment:
        template?.data?.who_to_pay_for_equipment || "agent",
      surcharge_per_transaction:
        template?.data?.surcharge_per_transaction ||
        pricingDefaults?.surcharge?.surcharge_per_transaction?.default ||
        "0.15",
      surcharge_qual_debit_rate:
        template?.data?.surcharge_qual_debit_rate ||
        pricingDefaults?.surcharge?.surcharge_qual_debit_rate?.default ||
        "1.50",
      flat_rate_amex_transaction:
        template?.data?.flat_rate_amex_transaction ||
        pricingDefaults?.flat_rate?.flat_rate_amex_transaction?.default ||
        "0.15",
      tiered_mid_qual_debit_rate:
        template?.data?.tiered_mid_qual_debit_rate ?? "",
      tiered_non_qual_debit_rate:
        template?.data?.tiered_non_qual_debit_rate ?? "",
      dual_pricing_pin_debit_rate:
        template?.data?.dual_pricing_pin_debit_rate ||
        pricingDefaults?.dual_pricing?.dual_pricing_pin_debit_rate?.default ||
        "0.00",
      tiered_mid_qual_transaction:
        template?.data?.tiered_mid_qual_transaction ?? "",
      tiered_non_qual_transaction:
        template?.data?.tiered_non_qual_transaction ?? "",
      ic_plus_amex_transaction_fee:
        template?.data?.ic_plus_amex_transaction_fee ||
        pricingDefaults?.ic_plus?.ic_plus_amex_transaction_fee?.default ||
        "0.15",
      tiered_amex_flat_transaction:
        template?.data?.tiered_amex_flat_transaction ||
        pricingDefaults?.tiered?.tiered_amex_flat_transaction?.default ||
        "0.20",
      tiered_pin_debit_network_fee:
        template?.data?.tiered_pin_debit_network_fee || "yes",
      ic_plus_pin_debit_network_fee:
        template?.data?.ic_plus_pin_debit_network_fee || "yes",
      pricing_approved_by_rate_desk:
        template?.data?.pricing_approved_by_rate_desk || "no",
      surcharge_mid_qual_debit_rate:
        template?.data?.surcharge_mid_qual_debit_rate || "1.99",
      surcharge_non_qual_debit_rate:
        template?.data?.surcharge_non_qual_debit_rate || "2.49",
      surcharge_signature_debit_rate:
        template?.data?.surcharge_signature_debit_rate || "3.00",
      flat_rate_pin_debit_network_fee:
        template?.data?.flat_rate_pin_debit_network_fee || "yes",
      surcharge_pin_debit_network_fee:
        template?.data?.surcharge_pin_debit_network_fee || "yes",
      tiered_amex_ic_plus_transaction:
        template?.data?.tiered_amex_ic_plus_transaction || "0.20",
      tiered_pin_debit_per_transaction:
        template?.data?.tiered_pin_debit_per_transaction ||
        pricingDefaults?.tiered?.tiered_pin_debit_per_transaction?.default ||
        "0.00",
      ic_plus_pin_debit_per_transaction:
        template?.data?.ic_plus_pin_debit_per_transaction ||
        pricingDefaults?.ic_plus?.ic_plus_pin_debit_per_transaction?.default ||
        "0.00",
      tiered_qual_debit_per_transaction:
        template?.data?.tiered_qual_debit_per_transaction ||
        pricingDefaults?.tiered?.tiered_qual_debit_per_transaction?.default ||
        "0.15",
      dual_pricing_pin_debit_network_fee:
        template?.data?.dual_pricing_pin_debit_network_fee || "yes",
      flat_rate_pin_debit_per_transaction:
        template?.data?.flat_rate_pin_debit_per_transaction ||
        pricingDefaults?.flat_rate?.flat_rate_pin_debit_per_transaction
          ?.default ||
        "0.15",
      surcharge_pin_debit_per_transaction:
        template?.data?.surcharge_pin_debit_per_transaction ||
        pricingDefaults?.surcharge?.surcharge_pin_debit_per_transaction
          ?.default ||
        "0.00",
      surcharge_qual_debit_per_transaction:
        template?.data?.surcharge_qual_debit_per_transaction ||
        pricingDefaults?.surcharge?.surcharge_qual_debit_per_transaction
          ?.default ||
        "0.15",
      tiered_mid_qual_debit_per_transaction:
        template?.data?.tiered_mid_qual_debit_per_transaction ?? "",
      tiered_non_qual_debit_per_transaction:
        template?.data?.tiered_non_qual_debit_per_transaction ?? "",
      dual_pricing_pin_debit_per_transaction:
        template?.data?.dual_pricing_pin_debit_per_transaction ||
        pricingDefaults?.dual_pricing?.dual_pricing_pin_debit_per_transaction
          ?.default ||
        "0.00",
      surcharge_mid_qual_debit_per_transaction:
        template?.data?.surcharge_mid_qual_debit_per_transaction ||
        pricingDefaults?.surcharge?.surcharge_mid_qual_debit_per_transaction
          ?.default ||
        "0.15",
      surcharge_non_qual_debit_per_transaction:
        template?.data?.surcharge_non_qual_debit_per_transaction ||
        pricingDefaults?.surcharge?.surcharge_non_qual_debit_per_transaction
          ?.default ||
        "0.15",
      surcharge_signature_debit_per_transaction:
        template?.data?.surcharge_signature_debit_per_transaction ||
        pricingDefaults?.surcharge?.surcharge_signature_debit_per_transaction
          ?.default ||
        "0.15",
      equipments,
      processors: templateHasProcessors
        ? template.data.processors
        : initialValues?.processors || [],
      tiered_amex_structure: template?.data?.pricing_model || "tiered",
      //ACH
      ach_by_web: template?.data?.ach_by_web || "no",
      ach_by_phone: template?.data?.ach_by_phone || "no",
      ach_by_preauth: template?.data?.ach_by_preauth || "no",
      ach_credit_transaction: template?.data?.ach_credit_transaction || "no",
      ach_discount_rate: template?.data?.ach_discount_rate || "",
      ach_transaction_fee_per_item:
        template?.data?.ach_transaction_fee_per_item ||
        pricingDefaults?.ach?.ach_transaction_fee_per_item?.default,
      ach_statement_fee:
        template?.data?.ach_statement_fee ||
        pricingDefaults?.ach?.ach_statement_fee?.default,
      ach_monthly_minimum:
        template?.data?.ach_monthly_minimum ||
        pricingDefaults?.ach?.ach_monthly_minimum?.default,
      ach_return_fee:
        template?.data?.ach_return_fee ||
        pricingDefaults?.ach?.ach_return_fee?.default,
      ach_noc_fee:
        template?.data?.ach_noc_fee ||
        pricingDefaults?.ach?.ach_noc_fee?.default,
      ach_unauthorized_return_fee:
        template?.data?.ach_unauthorized_return_fee ||
        pricingDefaults?.ach?.ach_unauthorized_return_fee?.default,
      ach_same_day_fee: template?.data?.ach_same_day_fee || "",
      ach_bank_validation_fee: template?.data?.ach_bank_validation_fee || "",
      add_ach: template?.data?.add_ach || "no",
      card_brand_fee:
        template?.data?.card_brand_fee ??
        pricingDefaults?.[templatePricingModel]?.card_brand_fee?.default ??
        "0.00",
      pci_non_compliance_fee:
        template?.data?.pci_non_compliance_fee ??
        pricingDefaults?.[templatePricingModel]?.pci_non_compliance_fee
          ?.default ??
        "0.00",

      // Set template applied flag
      templateApplied: true,
      nonEditableTemplateApplied: !template?.is_editable,
      processorSelectionLocked:
        !template?.is_editable && templateHasProcessors,
      mcc_id: template?.data?.mcc_id || initialValues?.mcc_id,
      ems_office_id: template?.data?.ems_office_id
        ? {
            label: template?.data?.ems_office_id?.label,
            value: template?.data?.ems_office_id?.label,
          }
        : null,
    });

    setOpenSections((prev) => ({
      ...prev,
      pricing: true,
      equipments: true,
    }));

    toast.success(`Template "${template.name}" applied successfully!`);
  };

  const getPricingValues = (prices, id) => {
    let price = prices.find((price) => price.id == id);
    if (price) {
      return price.pivot;
    }
    return {
      percent: null,
      dollars: null,
    };
  };

  const convertEquipment = (equipments) => {
    const converted_equipments = equipments.map((equip) => {
      const equipment = equip.terminal ? equip.terminal : equip.gateway;
      let type = null;

      if (equip.rent_for !== null) {
        type = "rent_for";
      } else if (equip.purchase_for !== null) {
        type = "purchase_for";
      } else if (equip.tpp !== null) {
        type = "apply_tpp";
      } else if (equip.lease_for !== null) {
        type = "lease_for";
      } else if (equip.monthly_fee !== null) {
        type = "monthly_fee";
      }

      return {
        ...equipment,
        monthly_fee_default: equip.monthly_fee,
        per_transaction_default: equip.per_transaction,
        purchase_for_default: equip.purchase_for,
        rent_for_default: equip.rent_for,
        apply_tpp_default: equip.tpp,
        hardware_default: equip.hardware_id,
        sass_fee_default: equip.saas_fee,
        lease_for_default: equip.lease_for,
        amount_default: equip.amount,
        factor_rate_default: equip.factor_rate,
        payback_default: equip.payback,
        other_equipments_default: equip.other_equipment_id,
        monthly_wireless_default: equip.monthly_wireless,
        text_sms_fee_default: equip.text_sms_fee,
        sim_card_monthly_default: equip.sim_card_monthly,
        connectivity_default: equip.connectivity,
        pin_pad_name_default: equip.pin_pad_name,
        monthly_cellular_txn_default: equip.monthly_cellular_txn,
        cellular_per_txn_default: equip.cellular_per_txn,
        nuvie_equipment_name: equip.nuvie_equipment_name,
        file_type_key: equip.file_type,
        package_key: equip.package,
        hardware_id: equip.hardware_id,
        other_equipment_id: equip.other_equipment_id,
        pin_debit_default: equip.pin_debit == 1 ? "yes" : "no",
        cable_length_key: equip.cable_length,
        sim_provided_by: equip.sim_provided_by,
        customer_facing_default: equip.customer_facing == 1 ? "yes" : "no",
        virtual_terminal_default: equip.virtual_terminal == 1 ? "yes" : "no",
        selected_addons: equip.selected_addons,
        type: type,
        quantity: equip.quantity,
      };
    });
    return converted_equipments;
  };

  const mapVarOnly = (var_only) => {
    if (var_only) {
      return {
        model: var_only?.terminal_model
          ? var_only?.terminal_model
          : var_only?.gateway_name
            ? var_only?.gateway_name
            : "",
        serial: var_only?.terminal_serial || "",
        type: var_only?.equipment_type || "",
      };
    }
    return null;
  };

  useEffect(() => {
    const fetchApplicationData = async () => {
      if (match?.params?.editslug) {
        setAppDataLoading(true);
        const [res, merchantInfo, banksResponse] = await Promise.all([
          getPreappById({ id: match?.params?.editslug }),
          getMerchantInfo({ id: match?.params?.editslug }),
          getOrganisationBanks(),
        ]);
        const equipments = res.data.ems_boot_equipments;
        const converted_equipments = convertEquipment(equipments);
        const qualifiedtype = merchantInfo.qualifiedtype;
        const tiered_amex_structure = merchantInfo.pricing_model;
        const prices = merchantInfo.prices;
        const billing = merchantInfo.billing;
        const var_only = res?.data?.var_only;
        const processors = mapOverrideBankToProcessorIds(
          res?.data?.override_bank,
          banksResponse?.status ? banksResponse.data : [],
        );

        const varOnlyData = mapVarOnly(var_only);

        setAppData(res.data);
        if (res?.data?.created_at && isAppArchived(res?.data?.created_at)) {
          setOpenArchiveApp(true);
          setIsArchivedApp(true);
          return;
        }

        setAppDataLoading(false);

        const defaultValuesForInactiveModels =
          getDefaultPricingModelValuesExceptActive(
            qualifiedtype || "",
            pricingDefaults,
          );

        setInitialValues(() => ({
          ...getInitialValues(pricingDefaults),
          pricing_model: qualifiedtype || "",
          processors,
          tiered_amex_toggle: tiered_amex_structure ? "yes" : "no",
          ic_plus_amex_toggle:
            getPricingValues(prices, 35).percent == 0 &&
            getPricingValues(prices, 35).dollars == 0
              ? "no"
              : "yes",
          // Dual pricing fields
          dual_pricing_rate:
            getPricingValues(prices, 35).percent ??
            pricingDefaults?.dual_pricing?.dual_pricing_rate?.default,
          dual_pricing_per_transaction:
            getPricingValues(prices, 35).dollars ??
            pricingDefaults?.dual_pricing?.dual_pricing_per_transaction
              ?.default,
          dual_pricing_pin_debit_network_fee:
            billing?.network_fee == "yes" ? "yes" : "no",
          dual_pricing_pin_debit_rate:
            getPricingValues(prices, 5).percent ??
            pricingDefaults?.dual_pricing?.dual_pricing_pin_debit_rate?.default,
          dual_pricing_pin_debit_per_transaction:
            getPricingValues(prices, 5).dollars ??
            pricingDefaults?.dual_pricing
              ?.dual_pricing_pin_debit_per_transaction?.default,
          dual_pricing_monthly_fee:
            getPricingValues(prices, 17).dollars ??
            pricingDefaults?.dual_pricing?.dual_pricing_monthly_fee?.default,

          // surcharge
          surcharge_rate:
            getPricingValues(prices, 35).percent ??
            pricingDefaults?.surcharge?.surcharge_rate?.default,
          surcharge_per_transaction:
            getPricingValues(prices, 35).dollars ??
            pricingDefaults?.surcharge?.surcharge_per_transaction?.default,
          surcharge_monthly_fee:
            getPricingValues(prices, 17).dollars ??
            pricingDefaults?.surcharge?.surcharge_monthly_fee?.default,
          surcharge_pin_debit_network_fee:
            billing?.network_fee == "yes" ? "yes" : "no",
          surcharge_pin_debit_rate:
            getPricingValues(prices, 5).percent ??
            pricingDefaults?.surcharge?.surcharge_pin_debit_rate?.default,
          surcharge_pin_debit_per_transaction:
            getPricingValues(prices, 5).dollars ??
            pricingDefaults?.surcharge?.surcharge_pin_debit_per_transaction
              ?.default,

          surcharge_qual_debit_rate:
            getPricingValues(prices, 39).percent ??
            pricingDefaults?.surcharge?.surcharge_qual_debit_rate?.default,
          surcharge_qual_debit_per_transaction:
            getPricingValues(prices, 39).dollars ??
            pricingDefaults?.surcharge?.surcharge_qual_debit_per_transaction
              ?.default,
          surcharge_mid_qual_debit_rate:
            getPricingValues(prices, 40).percent ??
            pricingDefaults?.surcharge?.surcharge_mid_qual_debit_rate?.default,
          surcharge_mid_qual_debit_per_transaction:
            getPricingValues(prices, 40).dollars ??
            pricingDefaults?.surcharge?.surcharge_mid_qual_debit_per_transaction
              ?.default,
          surcharge_non_qual_debit_rate:
            getPricingValues(prices, 41).percent ??
            pricingDefaults?.surcharge?.surcharge_non_qual_debit_rate?.default,
          surcharge_non_qual_debit_per_transaction:
            getPricingValues(prices, 41).dollars ??
            pricingDefaults?.surcharge?.surcharge_non_qual_debit_per_transaction
              ?.default,
          surcharge_signature_debit_rate:
            getPricingValues(prices, 6).percent ??
            pricingDefaults?.surcharge?.surcharge_signature_debit_rate?.default,
          surcharge_signature_debit_per_transaction:
            getPricingValues(prices, 6).dollars ??
            pricingDefaults?.surcharge
              ?.surcharge_signature_debit_per_transaction?.default,

          // Flat Rate
          flat_rate:
            getPricingValues(prices, 2).percent ??
            pricingDefaults?.flat_rate?.flat_rate?.default,
          flat_rate_transaction:
            getPricingValues(prices, 2).dollars ??
            pricingDefaults?.flat_rate?.flat_rate_transaction?.default,
          flat_rate_pin_debit_network_fee:
            billing?.network_fee == "yes" ? "yes" : "no",
          flat_rate_pin_debit_rate:
            getPricingValues(prices, 5).percent ??
            pricingDefaults?.flat_rate?.flat_rate_pin_debit_rate?.default,
          flat_rate_pin_debit_per_transaction:
            getPricingValues(prices, 5).dollars ??
            pricingDefaults?.flat_rate?.flat_rate_pin_debit_per_transaction
              ?.default,
          flat_rate_amex:
            getPricingValues(prices, 35).percent ??
            pricingDefaults?.flat_rate?.flat_rate_amex?.default,
          flat_rate_amex_transaction:
            getPricingValues(prices, 35).dollars ??
            pricingDefaults?.flat_rate?.flat_rate_amex_transaction?.default,

          // IC+
          ic_plus_rate:
            getPricingValues(prices, 2).percent ??
            pricingDefaults?.ic_plus?.ic_plus_rate?.default,
          ic_plus_transaction_fee:
            getPricingValues(prices, 2).dollars ??
            pricingDefaults?.ic_plus?.ic_plus_transaction_fee?.default,
          ic_plus_pin_debit_network_fee:
            billing?.network_fee == "yes" ? "yes" : "no",
          ic_plus_pin_debit_rate:
            getPricingValues(prices, 5).percent ??
            pricingDefaults?.ic_plus?.ic_plus_pin_debit_rate?.default,
          ic_plus_pin_debit_per_transaction:
            getPricingValues(prices, 5).dollars ??
            pricingDefaults?.ic_plus?.ic_plus_pin_debit_per_transaction
              ?.default,
          ic_plus_amex_rate:
            getPricingValues(prices, 35).percent ??
            pricingDefaults?.ic_plus?.ic_plus_amex_rate?.default,
          ic_plus_amex_transaction_fee:
            getPricingValues(prices, 35).dollars ??
            pricingDefaults?.ic_plus?.ic_plus_amex_transaction_fee?.default,

          // tiered
          tiered_qual_rate:
            getPricingValues(prices, 1).percent ??
            pricingDefaults?.tiered?.tiered_qual_rate?.default,
          tiered_qual_transaction:
            getPricingValues(prices, 1).dollars ??
            pricingDefaults?.tiered?.tiered_qual_transaction?.default,
          tiered_qual_debit_rate:
            getPricingValues(prices, 39).percent ??
            pricingDefaults?.tiered?.tiered_qual_debit_rate?.default,
          tiered_qual_debit_per_transaction:
            getPricingValues(prices, 39).dollars ??
            pricingDefaults?.tiered?.tiered_qual_debit_per_transaction?.default,
          tiered_mid_qual_rate: getPricingValues(prices, 3).percent ?? "",
          tiered_mid_qual_transaction:
            getPricingValues(prices, 3).dollars ?? "",
          tiered_non_qual_rate: getPricingValues(prices, 4).percent ?? "",
          tiered_non_qual_transaction:
            getPricingValues(prices, 4).dollars ?? "",
          tiered_mid_qual_debit_rate:
            getPricingValues(prices, 40).percent ?? "",
          tiered_mid_qual_debit_per_transaction:
            getPricingValues(prices, 40).dollars ?? "",
          tiered_non_qual_debit_rate:
            getPricingValues(prices, 41).percent ?? "",
          tiered_non_qual_debit_per_transaction:
            getPricingValues(prices, 41).dollars ?? "",
          tiered_pin_debit_network_fee:
            billing?.network_fee == "yes" ? "yes" : "no",
          tiered_pin_debit_rate:
            getPricingValues(prices, 5).percent ??
            pricingDefaults?.tiered?.tiered_pin_debit_rate?.default,
          tiered_pin_debit_per_transaction:
            getPricingValues(prices, 5).dollars ??
            pricingDefaults?.tiered?.tiered_pin_debit_per_transaction?.default,

          amex_tier_one_rate:
            getPricingValues(prices, 32).percent ??
            pricingDefaults?.tiered?.amex_tier_one_rate?.default,
          amex_tier_one_fee:
            getPricingValues(prices, 32).dollars ??
            pricingDefaults?.tiered?.amex_tier_one_fee?.default,
          amex_tier_two_rate: getPricingValues(prices, 33).percent ?? "",
          amex_tier_two_fee: getPricingValues(prices, 33).dollars ?? "",
          amex_tier_three_rate: getPricingValues(prices, 34).percent ?? "",
          amex_tier_three_fee: getPricingValues(prices, 34).dollars ?? "",
          amex_tier_four_rate: getPricingValues(prices, 49).percent ?? "",
          amex_tier_four_fee: getPricingValues(prices, 49).dollars ?? "",
          tiered_amex_flat_rate:
            getPricingValues(prices, 35).percent ??
            pricingDefaults?.tiered?.tiered_amex_flat_rate?.default,
          tiered_amex_flat_transaction:
            getPricingValues(prices, 35).dollars ??
            pricingDefaults?.tiered?.tiered_amex_flat_transaction?.default,
          tiered_amex_ic_plus_rate:
            getPricingValues(prices, 35).percent ??
            pricingDefaults?.tiered?.tiered_amex_ic_plus_rate?.default,
          tiered_amex_ic_plus_transaction:
            getPricingValues(prices, 35).dollars ??
            pricingDefaults?.tiered?.tiered_amex_ic_plus_transaction?.default,
          tiered_type: res?.data?.tiered_type ?? "swiped",

          // default fees
          setup_fee:
            getPricingValues(prices, 28).dollars ??
            pricingDefaults?.[qualifiedtype]?.setup_fee?.default ??
            pricingDefaults?.default_fees?.setup_fee?.default,
          monthly_access_fee:
            getPricingValues(prices, 19).dollars ??
            pricingDefaults?.[qualifiedtype]?.monthly_access_fee?.default ??
            pricingDefaults?.default_fees?.monthly_access_fee?.default,
          data_breach:
            getPricingValues(prices, 42).dollars ??
            pricingDefaults?.[qualifiedtype]?.data_breach?.default ??
            pricingDefaults?.default_fees?.data_breach?.default,
          monthly_minimum:
            getPricingValues(prices, 18).dollars ??
            pricingDefaults?.[qualifiedtype]?.monthly_minimum?.default ??
            pricingDefaults?.default_fees?.monthly_minimum?.default,
          semi_annual_tech_fee:
            getPricingValues(prices, 43).dollars ??
            pricingDefaults?.[qualifiedtype]?.semi_annual_tech_fee?.default ??
            pricingDefaults?.default_fees?.semi_annual_tech_fee?.default,
          voice_aru:
            getPricingValues(prices, 12).dollars ??
            pricingDefaults?.[qualifiedtype]?.voice_aru?.default ??
            pricingDefaults?.default_fees?.voice_aru?.default,
          batch_fee:
            getPricingValues(prices, 14).dollars ??
            pricingDefaults?.[qualifiedtype]?.batch_fee?.default ??
            pricingDefaults?.default_fees?.batch_fee?.default,
          returned_ach_item:
            getPricingValues(prices, 22).dollars ??
            pricingDefaults?.[qualifiedtype]?.returned_ach_item?.default ??
            pricingDefaults?.default_fees?.returned_ach_item?.default,
          avs_fee:
            getPricingValues(prices, 13).dollars ??
            pricingDefaults?.[qualifiedtype]?.avs_fee?.default ??
            pricingDefaults?.default_fees?.avs_fee?.default,
          auth_transaction:
            getPricingValues(prices, 11).dollars ??
            pricingDefaults?.[qualifiedtype]?.auth_transaction?.default ??
            pricingDefaults?.default_fees?.auth_transaction?.default,
          etf_fee:
            getPricingValues(prices, 46).dollars ??
            pricingDefaults?.[qualifiedtype]?.etf_fee?.default ??
            pricingDefaults?.default_fees?.etf_fee?.default,
          chargeback_fee:
            getPricingValues(prices, 20).dollars ??
            pricingDefaults?.[qualifiedtype]?.chargeback_fee?.default ??
            pricingDefaults?.default_fees?.chargeback_fee?.default,
          pre_dispute_fee:
            getPricingValues(prices, 48).dollars ??
            pricingDefaults?.[qualifiedtype]?.pre_dispute_fee?.default ??
            pricingDefaults?.default_fees?.pre_dispute_fee?.default,

          // kurv pricing
          kurv_pricing_swiped_rate:
            getPricingValues(prices, 58).percent ??
            pricingDefaults?.kurv_pricing?.kurv_pricing_swiped_rate?.default ??
            "",
          kurv_pricing_swiped_txn_fee:
            getPricingValues(prices, 58).dollars ??
            pricingDefaults?.kurv_pricing?.kurv_pricing_swiped_txn_fee
              ?.default ??
            "",
          kurv_pricing_keyed_rate:
            getPricingValues(prices, 59).percent ??
            pricingDefaults?.kurv_pricing?.kurv_pricing_keyed_rate?.default ??
            "",
          kurv_pricing_keyed_txn_fee:
            getPricingValues(prices, 59).dollars ??
            pricingDefaults?.kurv_pricing?.kurv_pricing_keyed_txn_fee
              ?.default ??
            "",
          kurv_pricing_amex_toggle:
            (!getPricingValues(prices, 60).percent &&
              !getPricingValues(prices, 60).dollars) ||
            (getPricingValues(prices, 60).percent == 0 &&
              getPricingValues(prices, 60).dollars == 0)
              ? "no"
              : "yes",
          kurv_pricing_amex_rate:
            getPricingValues(prices, 60).percent ??
            pricingDefaults?.kurv_pricing?.kurv_pricing_amex_rate?.default ??
            "",
          kurv_pricing_amex_txn_fee:
            getPricingValues(prices, 60).dollars ??
            pricingDefaults?.kurv_pricing?.kurv_pricing_amex_txn_fee?.default ??
            "",
          kurv_pricing_pin_debit_network_fee:
            billing?.network_fee == "yes" ? "yes" : "no",
          kurv_pricing_pin_debit_rate:
            getPricingValues(prices, 5).percent ??
            pricingDefaults?.kurv_pricing?.kurv_pricing_pin_debit_rate
              ?.default ??
            "",
          kurv_pricing_pin_debit_per_transaction:
            getPricingValues(prices, 5).dollars ??
            pricingDefaults?.kurv_pricing
              ?.kurv_pricing_pin_debit_per_transaction?.default ??
            "",

          //ach
          ach_discount_rate:
            getPricingValues(prices, 50).percent ??
            pricingDefaults?.ach?.ach_discount_rate?.default,
          ach_transaction_fee_per_item:
            getPricingValues(prices, 50).dollars ??
            pricingDefaults?.ach?.ach_transaction_fee_per_item?.default,
          ach_statement_fee:
            getPricingValues(prices, 51).dollars ??
            pricingDefaults?.ach?.ach_statement_fee?.default,
          ach_monthly_minimum:
            getPricingValues(prices, 52).dollars ??
            pricingDefaults?.ach?.ach_monthly_minimum?.default,
          ach_return_fee:
            getPricingValues(prices, 53).dollars ??
            pricingDefaults?.ach?.ach_return_fee?.default,
          ach_noc_fee:
            getPricingValues(prices, 54).dollars ??
            pricingDefaults?.ach?.ach_noc_fee?.default,
          ach_unauthorized_return_fee:
            getPricingValues(prices, 55).dollars ??
            pricingDefaults?.ach?.ach_unauthorized_return_fee?.default,
          ach_same_day_fee:
            getPricingValues(prices, 56).dollars ??
            pricingDefaults?.ach?.ach_same_day_fee?.default,
          ach_bank_validation_fee:
            getPricingValues(prices, 57).dollars ??
            pricingDefaults?.ach?.ach_bank_validation_fee?.default,

          ach_by_web: billing?.ach_by_web == 1 ? "yes" : "no",
          ach_by_phone: billing?.ach_by_phone == 1 ? "yes" : "no",
          ach_by_preauth: billing?.ach_by_preauth == 1 ? "yes" : "no",
          ach_credit_transaction:
            billing?.ach_credit_transaction == 1 ? "yes" : "no",
          add_ach: res?.data?.is_ach ? "yes" : "no",

          //Note: Below field is not used anywhere, but kept so as not to break anything.
          monthly_fee: getPricingValues(prices, 17).dollars || 49.0,

          // ebt
          ebt_transactions:
            getPricingValues(prices, 29).dollars ??
            pricingDefaults?.[qualifiedtype]?.ebt_transactions?.default ??
            pricingDefaults?.default_fees?.ebt_transactions?.default,
          ebt: billing?.is_ebt == 1 ? "yes" : "no",
          fnf_number: billing?.fnf_number,

          var_sheet_request: billing?.var_sheet_request === "1" ? true : false,

          equipments: converted_equipments,
          var_only: varOnlyData,

          who_to_deploy_equipment: billing?.who_to_deploy_equipment || "",
          who_to_pay_for_equipment: billing?.who_to_pay_for_equipment || "",
          who_to_deploy_gateway: billing?.who_to_deploy_gateway || "",
          billing_sequence: billing?.billing_sequence || "daily",
          cover_cancellation: billing?.reimbursement_maximum ? "yes" : "no",
          reimbursement_maximum: billing?.reimbursement_maximum || "",
          expedited_funding: billing?.expedited_funding || "no",
          batch_time: billing?.batch_time || "06:00 PM",
          equipment_address: billing?.equipment_address || "",
          invoice_address: billing?.invoice_address || "",
          special_instructions: billing?.special_instruction || "",
          uw_notes: res?.data?.uw_notes || "",
          is_guarantor: res?.data?.is_guarantor != 0,
          mcc_id: res?.data.mcc_info?.description
            ? {
                label: res?.data?.mcc_info?.description,
                value: res?.data?.mcc_info?.id,
              }
            : null,
          ems_office_id: res?.data?.ems_office
            ? {
                label: res?.data?.ems_office?.office_id,
                value: res?.data?.ems_office?.office_id,
              }
            : null,
          payliance_industry:
            res?.data?.powerappbusinessprofile?.payliance_industry || "",

          ...defaultValuesForInactiveModels,
          // Set tiered_amex_structure after defaultValuesForInactiveModels to prevent it from being overwritten
          tiered_amex_structure: tiered_amex_structure || "tiered",
          // new default fees
          card_brand_fee:
            getPricingValues(prices, 61).dollars ??
            pricingDefaults?.[qualifiedtype]?.card_brand_fee?.default,
          pci_non_compliance_fee:
            getPricingValues(prices, 62).dollars ??
            pricingDefaults?.[qualifiedtype]?.pci_non_compliance_fee?.default,
          retrieval_fee:
            getPricingValues(prices, 21).dollars ??
            pricingDefaults?.[qualifiedtype]?.retrieval_fee?.default,
          representment_fee:
            getPricingValues(prices, 47).dollars ??
            pricingDefaults?.[qualifiedtype]?.representment_fee?.default ??
            pricingDefaults?.default_fees?.representment_fee?.default,
        }));
        setSelectedTemplate(
          res?.data?.template_id ? res?.data?.template_id : "",
        );
      }
    };

    fetchApplicationData();
  }, [match?.params?.editslug, pricingDefaults]);

  const dataLoading =
    appDataLoading || pricingDefaultsLoading || templateListLoading;

  return (
    <div className={styles.page}>
      <Breadcrumbs
        items={[
          { label: "Application", to: "/applications" },
          { label: "Edit Pricing & Equipment" },
        ]}
      />

      {!isArchivedApp && (
        <h1 className={styles.title}>Edit Pricing & Equipment</h1>
      )}
      {!isArchivedApp && (
        <Formik
          enableReinitialize={true}
          validationSchema={createFormSchema(pricingDefaults)}
          onSubmit={onSubmit}
          initialValues={initialValues}
          validateOnBlur={true}
          validateOnChange={true}
        >
          {({
            handleSubmit,
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            setValues,
            validateForm,
            setTouched,
          }) => {
            const handleButtonClick = async (btnText) => {
              const kurvMerchant = values?.equipments.find(
                (item) =>
                  item.id === config.paymentcloud.KURV_MERCHANT_EQUIPMENT_ID,
              );
              const kurvProcessorSelected = values?.processors?.includes(
                config.paymentcloud.KURV_PROCESSOR_ID,
              );

              let kurvMerchantAutoAdded = false;
              let currentEquipments = values?.equipments || [];
              if ((isKurvUser || kurvProcessorSelected) && !kurvMerchant) {
                const firstMatchEquipment = equipments.find(
                  (item) =>
                    item.id === config.paymentcloud.KURV_MERCHANT_EQUIPMENT_ID,
                );
                if (firstMatchEquipment && firstMatchEquipment?.id) {
                  setFieldValue(
                    "equipments",
                    [
                      {
                        ...firstMatchEquipment,
                        type: "monthly_fee",
                        quantity: 1,
                      },
                      ...values.equipments,
                    ],
                    true,
                  );
                  currentEquipments = [
                    {
                      ...firstMatchEquipment,
                      type: "monthly_fee",
                      quantity: 1,
                    },
                    ...values.equipments,
                  ];
                  kurvMerchantAutoAdded = true;
                }
              }

              let allErrors = await validateForm();

              const hasPricingErrors = !createPricingInfoSchema(
                pricingDefaults,
                { requirePaylianceIndustry: true },
              ).isValidSync(values);
              const hasEquipmentErrors =
                !kurvMerchantAutoAdded &&
                (allErrors.equipments || currentEquipments.length === 0);
              const hasDetailsErrors = !detailsInfoSchema.isValidSync(values);
              const hasProcessorsErrors =
                !processorInfoSchema.isValidSync(values);

              setOpenSections({
                pricing: hasPricingErrors,
                equipments: hasEquipmentErrors,
                details: hasDetailsErrors,
                processors: hasProcessorsErrors,
              });

              // mimicking formik submit behavior
              setTouched(
                Object.keys(values).reduce((acc, key) => {
                  acc[key] = true;
                  return acc;
                }, {}),
              );

              scrollToError(allErrors);

              const shouldShowConfirmation = shouldConfirmOnlyKurvEquipment({
                equipments: currentEquipments,
                processors: values?.processors || [],
                organizationId,
              });

              if (shouldShowConfirmation) {
                delete allErrors.equipments;
              }

              if (Object.keys(allErrors).length > 0) {
                return;
              }

              if (shouldShowConfirmation) {
                setButtonText(btnText);
                setConfirmId(currentEquipments[0].id);
              } else {
                handleSubmit();
              }
            };

            const sendToSubmit = () => {
              setConfirmId(null);
              handleSubmit();
            };

            console.log(values);
            console.log(errors);
            return (
              <div className={styles.content}>
                <div className={styles.left}>
                  <form className={styles.form}>
                    <div className={styles.template_section}>
                      <div className={styles.text}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M8 1.5C6.71442 1.5 5.45772 1.88122 4.3888 2.59545C3.31988 3.30968 2.48676 4.32484 1.99479 5.51256C1.50282 6.70028 1.37409 8.00721 1.6249 9.26809C1.8757 10.529 2.49477 11.6872 3.40381 12.5962C4.31285 13.5052 5.47104 14.1243 6.73192 14.3751C7.99279 14.6259 9.29973 14.4972 10.4874 14.0052C11.6752 13.5132 12.6903 12.6801 13.4046 11.6112C14.1188 10.5423 14.5 9.28558 14.5 8C14.4982 6.27665 13.8128 4.62441 12.5942 3.40582C11.3756 2.18722 9.72335 1.50182 8 1.5ZM7.75 4.5C7.89834 4.5 8.04334 4.54399 8.16668 4.6264C8.29002 4.70881 8.38615 4.82594 8.44291 4.96299C8.49968 5.10003 8.51453 5.25083 8.48559 5.39632C8.45665 5.5418 8.38522 5.67544 8.28033 5.78033C8.17544 5.88522 8.04181 5.95665 7.89632 5.98559C7.75083 6.01453 7.60003 5.99968 7.46299 5.94291C7.32595 5.88614 7.20881 5.79001 7.1264 5.66668C7.04399 5.54334 7 5.39834 7 5.25C7 5.05109 7.07902 4.86032 7.21967 4.71967C7.36032 4.57902 7.55109 4.5 7.75 4.5ZM8.5 11.5C8.23479 11.5 7.98043 11.3946 7.7929 11.2071C7.60536 11.0196 7.5 10.7652 7.5 10.5V8C7.36739 8 7.24022 7.94732 7.14645 7.85355C7.05268 7.75979 7 7.63261 7 7.5C7 7.36739 7.05268 7.24021 7.14645 7.14645C7.24022 7.05268 7.36739 7 7.5 7C7.76522 7 8.01957 7.10536 8.20711 7.29289C8.39465 7.48043 8.5 7.73478 8.5 8V10.5C8.63261 10.5 8.75979 10.5527 8.85356 10.6464C8.94732 10.7402 9 10.8674 9 11C9 11.1326 8.94732 11.2598 8.85356 11.3536C8.75979 11.4473 8.63261 11.5 8.5 11.5Z"
                            fill="#626168"
                          />
                        </svg>
                        <span>
                          Speed up the app by using a Pricing + Equipment
                          template
                        </span>
                      </div>
                      <div className={styles.right}>
                        {templateListLoading ? (
                          <SkeletonLoader
                            variant="rectangular"
                            height="32px"
                            width="200px"
                            component="div"
                            style={{ borderRadius: "8px" }}
                          />
                        ) : (
                          <Select
                            label=""
                            placeholder="Apply Template"
                            id="apply_template"
                            name="apply_template"
                            options={templateOptions}
                            search
                            customClass={styles.templateSelect}
                            value={selectedTemplate}
                            onChange={(value) => {
                              setSelectedTemplate(value);
                              if (value) {
                                if (!isTemplateChanged) {
                                  setIsTemplateChanged(true);
                                  setInitialValuesBackup(initialValues);
                                }
                                applyTemplate(value, setValues);
                              } else {
                                setFieldValue("templateApplied", false);
                                setFieldValue(
                                  "nonEditableTemplateApplied",
                                  false,
                                );
                                setValues(initialValuesBackup);
                              }
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {values.nonEditableTemplateApplied && (
                      <Alert fullWidth type="warning">
                        A non-editable template has been applied
                      </Alert>
                    )}

                    {dataLoading && (
                      <SkeletonLoader
                        variant="rectangular"
                        component="div"
                        height="150px"
                        width="100%"
                        style={{ borderRadius: "24px" }}
                      />
                    )}

                    {dataLoading && (
                      <SkeletonLoader
                        variant="rectangular"
                        component="div"
                        height="1400px"
                        width="100%"
                        style={{ borderRadius: "24px" }}
                      />
                    )}

                    {dataLoading && (
                      <SkeletonLoader
                        variant="rectangular"
                        component="div"
                        height="1000px"
                        width="100%"
                        style={{ borderRadius: "24px" }}
                      />
                    )}

                    {dataLoading && (
                      <SkeletonLoader
                        variant="rectangular"
                        component="div"
                        height="600px"
                        width="100%"
                        style={{ borderRadius: "24px" }}
                      />
                    )}

                    {!dataLoading && (
                      <>
                        {(isPCUser ||
                          (isKurvUser && emsOfficeOptions?.length > 1)) && (
                          <MCCAndEMSOfficeId
                            isOpen={openSections.mccAndEMSOfficeId}
                            onToggle={() =>
                              setOpenSections((prev) => ({
                                ...prev,
                                mccAndEMSOfficeId: !prev.mccAndEMSOfficeId,
                              }))
                            }
                            emsOfficeOptions={emsOfficeOptions}
                          />
                        )}

                        <Processors
                          isOpen={openSections.processors}
                          onToggle={() =>
                            setOpenSections((prev) => ({
                              ...prev,
                              processors: !prev.processors,
                            }))
                          }
                        />

                        <Pricing
                          isOpen={openSections.pricing}
                          onToggle={() =>
                            setOpenSections((prev) => ({
                              ...prev,
                              pricing: !prev.pricing,
                            }))
                          }
                          showPaylianceIndustry
                        />

                        <Equipments
                          isOpen={openSections.equipments}
                          onToggle={() =>
                            setOpenSections((prev) => ({
                              ...prev,
                              equipments: !prev.equipments,
                            }))
                          }
                        />

                        <Details
                          isOpen={openSections.details}
                          onToggle={() =>
                            setOpenSections((prev) => ({
                              ...prev,
                              details: !prev.details,
                            }))
                          }
                          orgEnablesPersonalGuarantor={
                            !!appData?.organization?.enable_personal_guarantor_toggle
                          }
                          canManagePersonalGuarantor={
                            isEntitySuperAdmin ||
                            appData?.user_id === user?.id ||
                            (user?.role === "admin" &&
                              user?.organization_id === appData?.organization_id)
                          }
                        />
                      </>
                    )}

                    <div className={styles.buttons}>
                      <ButtonIcon
                        bgColor={"#FFF"}
                        textColor="#254FE2"
                        onClick={async () => {
                          setSubmissionType("create_app");
                          await handleButtonClick("Save");
                        }}
                        disabled={isSubmitting || dataLoading}
                      >
                        Save
                      </ButtonIcon>

                      <ButtonIcon
                        onClick={async () => {
                          setSubmissionType("pe_send_to_merchant");
                          await handleButtonClick("Save");
                        }}
                        bgColor={isKurvDomain ? "#22A276" : "#4c70ef"}
                        textColor="#fff"
                        isLoading={sendingLinkToMerchant}
                      >
                        Send to Merchant
                      </ButtonIcon>
                      {appData?.lead_progression_id >= 3 && (
                        <ButtonIcon
                          bgColor={"#4C70EF"}
                          textColor="#FFFFFF"
                          onClick={async () => {
                            setSubmissionType("send_app_for_signature");
                            await handleButtonClick("Send App for Signature");
                          }}
                          disabled={
                            isSubmitting ||
                            Object.keys(errors).length > 0 ||
                            dataLoading
                          }
                        >
                          Send App for Signature
                        </ButtonIcon>
                      )}
                    </div>
                  </form>
                </div>
                <div className={styles.right}>
                  <Cost />

                  <div className={styles.mobileFooter}>
                    <div className={styles.buttons}>
                      <ButtonIcon
                        bgColor={"#FFF"}
                        textColor="#254FE2"
                        onClick={async () => {
                          setSubmissionType("create_app");
                          await handleButtonClick("Save");
                        }}
                        disabled={isSubmitting || dataLoading}
                      >
                        Save
                      </ButtonIcon>
                      <ButtonIcon
                        onClick={async () => {
                          setSubmissionType("pe_send_to_merchant");
                          await handleButtonClick("Save");
                        }}
                        bgColor={isKurvDomain ? "#22A276" : "#4c70ef"}
                        textColor="#fff"
                        isLoading={sendingLinkToMerchant}
                      >
                        Send to Merchant
                      </ButtonIcon>
                      {appData?.lead_progression_id >= 3 && (
                        <ButtonIcon
                          bgColor={"#4C70EF"}
                          textColor="#FFFFFF"
                          onClick={async () => {
                            setSubmissionType("send_app_for_signature");
                            await handleButtonClick("Send App for Signature");
                          }}
                          disabled={
                            isSubmitting ||
                            Object.keys(errors).length > 0 ||
                            dataLoading
                          }
                        >
                          Send App for Signature
                        </ButtonIcon>
                      )}
                    </div>
                  </div>
                </div>

                <SendLinkMerchantModal
                  onClose={() => setIsSendToMerchantModalOpen(false)}
                  isOpen={isSendToMerchantModalOpen}
                  onContinue={() => {
                    setIsSendToMerchantModalOpen(false);
                  }}
                  applicationURL={applicationURL}
                  continueBtnTxt={"Cancel"}
                />
                <ConfirmationModal
                  open={showSendToMerchantConfirm}
                  onClose={() => {
                    setShowSendToMerchantConfirm(false);
                    setSendingLinkToMerchant(false);
                  }}
                  message="Are you sure you want to send the application link to the merchant?"
                  onConfirmClick={confirmSendToMerchant}
                  submitButtonText="Send"
                  confirmButtonColor="blue"
                />
                <ConfirmationModal
                  open={!!confirmId}
                  onClose={() => setConfirmId(null)}
                  message={
                    "No equipment selected - Kurv Merchant applied. Please confirm."
                  }
                  submitButtonText={`Confirm & ${buttonText}`}
                  cancelButtonText={"Go Back"}
                  onConfirmClick={() => sendToSubmit()}
                />
              </div>
            );
          }}
        </Formik>
      )}
      {/* Archive popup */}
      {openArchiveApp && (
        <ArchiveApp
          open={openArchiveApp}
          onClose={handleArchiveClose}
          onDuplicate={() => {
            setOpenArchiveApp(false);
            setConfirmCopyApplication(true);
          }}
        />
      )}
      {/* Duplicate confirmation */}
      {confirmCopyApplication && (
        <CopyApplication
          appData={appData}
          isOpen={confirmCopyApplication}
          onClose={() => setConfirmCopyApplication(false)}
          loggedInUser={user}
          history={history}
        />
      )}
      {/* Toaster global component */}
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            borderRadius: "8px",
            background: "rgba(40, 39, 45, 0.85)",
            color: "#fff",
            padding: "16px 24px",
          },
          duration: 5000,
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <>
                {icon}
                {message}
                <button
                  className={styles.toast_dismissal_button}
                  onClick={() => toast.dismiss(t.id)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M12.8535 12.1462C12.9 12.1927 12.9368 12.2478 12.962 12.3085C12.9871 12.3692 13.0001 12.4343 13.0001 12.5C13.0001 12.5657 12.9871 12.6307 12.962 12.6914C12.9368 12.7521 12.9 12.8073 12.8535 12.8537C12.8071 12.9002 12.7519 12.937 12.6912 12.9622C12.6305 12.9873 12.5655 13.0003 12.4998 13.0003C12.4341 13.0003 12.369 12.9873 12.3083 12.9622C12.2476 12.937 12.1925 12.9002 12.146 12.8537L7.99979 8.70685L3.85354 12.8537C3.75972 12.9475 3.63247 13.0003 3.49979 13.0003C3.36711 13.0003 3.23986 12.9475 3.14604 12.8537C3.05222 12.7599 2.99951 12.6327 2.99951 12.5C2.99951 12.3673 3.05222 12.24 3.14604 12.1462L7.29291 7.99997L3.14604 3.85372C3.05222 3.7599 2.99951 3.63265 2.99951 3.49997C2.99951 3.36729 3.05222 3.24004 3.14604 3.14622C3.23986 3.0524 3.36711 2.99969 3.49979 2.99969C3.63247 2.99969 3.75972 3.0524 3.85354 3.14622L7.99979 7.2931L12.146 3.14622C12.2399 3.0524 12.3671 2.99969 12.4998 2.99969C12.6325 2.99969 12.7597 3.0524 12.8535 3.14622C12.9474 3.24004 13.0001 3.36729 13.0001 3.49997C13.0001 3.63265 12.9474 3.7599 12.8535 3.85372L8.70666 7.99997L12.8535 12.1462Z"
                      fill="white"
                    />
                  </svg>
                </button>
              </>
            )}
          </ToastBar>
        )}
      </Toaster>
    </div>
  );
};

export default withRouter(EditPricingEquipmentPage);
